import React, { useEffect, useState } from "react";
import * as Mui from "@mui/material";
import moment from "moment";
import { useSelector } from "react-redux";
import { CheckRounded, DescriptionOutlined, ErrorOutlineOutlined, FileDownloadOutlined, FunctionsOutlined, HourglassEmptyOutlined, PendingActionsOutlined } from "@mui/icons-material";
import { showToast } from "services/utils/Status";
import { GlobalSidePanel } from "components/DesignSystem/GlobalSidePanel";
import { RequestInfoBanner } from "components/DesignSystem/RequestInfoBanner";
import { RequestDetailsSection } from "components/DesignSystem/RequestDetailsSection";
import { detailAccentPalette } from "components/DesignSystem/RequestDetailField";
import { GlobalConfirmDialog } from "components/DesignSystem/sharedUiPatterns";
import { PanelActionFooter, PrimaryButton, SecondaryButton } from "components/CustomComponents/DesignSystemButtons";
import { useSavedViewSelector } from "components/CustomComponents/useSavedViewSelector";
import { Loader } from "components/Loader";
import { designColors, designType, designWeights } from "services/constants/designTokens";
import { ptPaymentHeader } from "pages/PayRoll/PayRollHeaders";
import { Field, FormSection, panelBodySx } from "pages/PayRoll/PayInputs/Shared/formUi";
import { DateField, SelectField, TextField } from "pages/PayRoll/PayInputs/Shared/fieldInputs";
import { EmptyRow, Required, SimpleTable, ToneChip, tableCellSx } from "pages/PayRoll/Salary/Shared/salaryUi";
import { FilterSelect, PlainInput, RecordHeaderCard, StatCardGrid, StatutoryList } from "pages/PayRoll/Shared/statutoryUi";
import {
  downloadPtStatement,
  fetchPtPaymentPeriod,
  fetchPtPayments,
  fetchPtStatement,
  previewPtPayment,
  recordPtPayment,
  reversePtPayment,
} from "./ptReportsApi";

// Payments (PT5) from GET payments/: one row per site per PT period (month,
// half-year or year). Payable is never typed — it comes from finalised
// payroll. Cards filter the loaded rows on screen (the API returns every
// period in the range). The period drawer (GET payments/<id>/) shows the
// payments with reversals and the Company PT statement; Record PT Payment
// (PT5a) shows the server's live status line and saves with POST record/.
// A saved payment is never edited: it is reversed and recorded again.

const { indigo, green, orange } = detailAccentPalette;
const linkSx = { color: designColors.tealDark, fontWeight: designWeights.semibold, whiteSpace: "nowrap" };
const ALL = "all";
const PREVIEW_DELAY = 300;

const PERIOD_FILTERS = [
  { value: "last_12", label: "Last 12 months" },
  { value: "last_24", label: "Last 24 months" },
  { value: "this_fy", label: "This financial year" },
  { value: "custom", label: "Custom" },
];
const PAYMENT_TYPES = [
  { value: "FIRST", label: "First payment" },
  { value: "BALANCE", label: "Balance payment" },
];
// Card -> statuses it shows (guide PT5).
const CARD_STATUSES = {
  topay: ["TO_PAY", "PARTLY_PAID"],
  short: ["PARTLY_PAID"],
  paid: ["PAID"],
  collecting: ["COLLECTING", "NOT_DUE_YET"],
};

const money = (value) => (value == null ? "—" : `₹${Number(value).toLocaleString("en-IN")}`);
const num = (value) => (value == null ? "—" : Number(value).toLocaleString("en-IN"));
const day = (value) => (value ? moment(value).format("DD MMM YYYY") : "—");

// ---------------- Company PT statement ----------------

const StatementCard = ({ domain, companyId, period }) => {
  const [open, setOpen] = useState(false);
  const [statement, setStatement] = useState({ loading: false, error: "", data: null });
  const [menu, setMenu] = useState(null);

  useEffect(() => {
    setOpen(false);
    setStatement({ loading: false, error: "", data: null });
  }, [period.id]);

  useEffect(() => {
    if (!open || statement.data || statement.loading) return undefined;
    const controller = new AbortController();
    setStatement({ loading: true, error: "", data: null });
    fetchPtStatement({ domain, companyId, periodId: period.id, signal: controller.signal })
      .then((data) => setStatement({ loading: false, error: "", data }))
      .catch((error) => {
        if (!controller.signal.aborted) setStatement({ loading: false, error: error.message || "Could not load the statement.", data: null });
      });
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, domain, companyId, period.id]);

  const download = async (format) => {
    setMenu(null);
    try {
      await downloadPtStatement({ domain, companyId, periodId: period.id, format });
    } catch (error) {
      showToast("error", error.message || "Couldn't download the statement.");
    }
  };

  const data = statement.data;
  return (
    <Mui.Box sx={{ p: 2, border: `1px solid ${designColors.slate200}`, borderRadius: 2, backgroundColor: designColors.white }}>
      <Mui.Stack direction="row" spacing={1.5} alignItems="flex-start" justifyContent="space-between">
        <Mui.Stack direction="row" spacing={1.25} alignItems="flex-start" sx={{ minWidth: 0 }}>
          <DescriptionOutlined sx={{ color: designColors.tealDark, mt: 0.25 }} />
          <Mui.Box sx={{ minWidth: 0 }}>
            <Mui.Typography sx={{ ...designType.tableBodyCell, fontWeight: designWeights.semibold }}>Company PT statement</Mui.Typography>
            <Mui.Typography sx={designType.helperText}>
              {num(period.employees)} employees · {money(period.payable)} · prepared by your company — not an official return
            </Mui.Typography>
          </Mui.Box>
        </Mui.Stack>
        <Mui.Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
          <SecondaryButton onClick={() => setOpen((value) => !value)} sx={{ minHeight: 32, whiteSpace: "nowrap" }}>
            {open ? "Hide" : "View"}
          </SecondaryButton>
          <SecondaryButton startIcon={<FileDownloadOutlined />} onClick={(event) => setMenu(event.currentTarget)} sx={{ minHeight: 32, whiteSpace: "nowrap" }}>
            Download
          </SecondaryButton>
          <Mui.Menu anchorEl={menu} open={Boolean(menu)} onClose={() => setMenu(null)}>
            <Mui.MenuItem onClick={() => download("xlsx")}>Excel (.xlsx)</Mui.MenuItem>
            <Mui.MenuItem onClick={() => download("csv")}>CSV</Mui.MenuItem>
            <Mui.MenuItem onClick={() => download("pdf")}>PDF</Mui.MenuItem>
          </Mui.Menu>
        </Mui.Stack>
      </Mui.Stack>
      {open ? (
        <Mui.Box sx={{ mt: 1.5 }}>
          {statement.loading ? (
            <Loader info="Loading..." minHeight="20vh" />
          ) : statement.error ? (
            <RequestInfoBanner tone="danger" body={statement.error} />
          ) : data ? (
            <Mui.Stack spacing={1}>
              <Mui.Typography sx={designType.helperText}>
                {[data.header.company, data.header.site, data.header.registration_no, [data.header.state, data.header.local_body].filter(Boolean).join(" · "), data.header.period, data.header.version]
                  .filter(Boolean)
                  .join(" · ")}
              </Mui.Typography>
              <SimpleTable
                fit
                heads={[
                  { label: "Employee", width: 160 },
                  { label: "Months", width: 130 },
                  { label: "PT wage (₹)", width: 100, align: "right" },
                  { label: "PT (₹)", width: 80, align: "right" },
                  { label: "Parts", width: 90 },
                ]}
              >
                {data.rows.length ? (
                  data.rows.map((row) => (
                    <Mui.TableRow key={row.id}>
                      <Mui.TableCell sx={tableCellSx}>{[row.name, row.emp_id].filter(Boolean).join(" · ")}</Mui.TableCell>
                      <Mui.TableCell sx={tableCellSx}>{row.months_label || "—"}</Mui.TableCell>
                      <Mui.TableCell align="right" sx={tableCellSx}>{num(row.pt_wage)}</Mui.TableCell>
                      <Mui.TableCell align="right" sx={tableCellSx}>{num(row.pt)}</Mui.TableCell>
                      <Mui.TableCell sx={tableCellSx}>{row.parts_label || "—"}</Mui.TableCell>
                    </Mui.TableRow>
                  ))
                ) : (
                  <EmptyRow colSpan={5} text="No employees with PT in this period." />
                )}
              </SimpleTable>
              <Mui.Typography sx={{ ...designType.tableBodyCell, fontWeight: designWeights.semibold, textAlign: "right" }}>
                {num(data.totals.employees)} employees · PT {money(data.totals.pt)} · paid {money(data.totals.paid)} · balance {money(data.totals.balance)}
              </Mui.Typography>
              {data.header.note ? <Mui.Typography sx={designType.captionText}>{data.header.note}</Mui.Typography> : null}
            </Mui.Stack>
          ) : null}
        </Mui.Box>
      ) : null}
    </Mui.Box>
  );
};

// Loads one period (GET payments/<id>/) for a drawer.
const usePeriod = ({ open, domain, companyId, periodId, reloadKey }) => {
  const [state, setState] = useState({ loading: true, error: "", data: null });
  useEffect(() => {
    if (!open || periodId == null) return undefined;
    const controller = new AbortController();
    setState((current) => ({ loading: true, error: "", data: current.data?.id === periodId ? current.data : null }));
    fetchPtPaymentPeriod({ domain, companyId, periodId, signal: controller.signal })
      .then((data) => setState({ loading: false, error: "", data }))
      .catch((error) => {
        if (!controller.signal.aborted) setState({ loading: false, error: error.message || "Could not load the payment period.", data: null });
      });
    return () => controller.abort();
  }, [open, domain, companyId, periodId, reloadKey]);
  return state;
};

const PeriodHeader = ({ period }) => (
  <RecordHeaderCard
    avatar="PT"
    title={period.title}
    chip={<ToneChip label={period.status_label} tone={period.status_tone} />}
    meta={[
      `${num(period.employees)} employees`,
      period.balance ? `amount due ${money(period.balance)}` : null,
      period.due ? `due ${day(period.due)}` : null,
    ]
      .filter(Boolean)
      .join(" · ")}
    tone={period.status === "PAID" ? "teal" : ["TO_PAY", "PARTLY_PAID"].includes(period.status) ? "warning" : "slate"}
  />
);

// ---------------- Record PT Payment (PT5a) ----------------

const RecordPanel = ({ open, domain, companyId, periodId, onClose, onSaved }) => {
  const { loading, error: loadError, data: period } = usePeriod({ open, domain, companyId, periodId, reloadKey: 0 });
  const [form, setForm] = useState(null);
  const [showErrors, setShowErrors] = useState(false);
  const [serverErrors, setServerErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    if (!open || !period) return;
    setForm({ type: period.next_type, paid_on: "", amount: period.balance > 0 ? String(period.balance) : "", reference: "", remarks: "" });
    setShowErrors(false);
    setServerErrors({});
  }, [open, period]);

  // Live line under the amount (saves nothing).
  const amountText = form?.amount ?? "";
  useEffect(() => {
    if (!open || !period || !(Number(amountText) > 0)) {
      setPreview(null);
      return undefined;
    }
    const controller = new AbortController();
    const timer = setTimeout(() => {
      previewPtPayment({ domain, companyId, periodId: period.id, amount: amountText, signal: controller.signal })
        .then(setPreview)
        .catch(() => {
          if (!controller.signal.aborted) setPreview(null);
        });
    }, PREVIEW_DELAY);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [open, domain, companyId, period, amountText]);

  const set = (key) => (value) => {
    setForm((current) => ({ ...current, [key]: value }));
    setServerErrors((current) => ({ ...current, [key]: undefined }));
  };

  const errors = {};
  if (form) {
    const amount = String(form.amount).trim();
    if (!form.type) errors.type = "Choose first payment or balance payment";
    if (!form.paid_on) errors.paid_on = "Enter the date paid";
    else if (moment(form.paid_on).isAfter(moment(), "day")) errors.paid_on = "The date paid can't be in the future";
    else if (period?.start_month && moment(form.paid_on).isBefore(moment(period.start_month).startOf("month"), "day"))
      errors.paid_on = `The date paid can't be before ${moment(period.start_month).format("MMM YYYY")}`;
    if (!amount) errors.amount = "Enter the amount paid";
    else if (!(Number(amount) > 0)) errors.amount = "Amount must be more than 0";
    else if (!/^\d+(\.\d{1,2})?$/.test(amount)) errors.amount = "Up to 2 decimals";
    if (!form.reference.trim()) errors.reference = "Enter the payment reference";
    else if (form.reference.trim().length > 60) errors.reference = "60 characters at most";
  }
  const error = (key) => (showErrors ? errors[key] : undefined) || serverErrors[key];

  const save = async () => {
    setShowErrors(true);
    if (Object.keys(errors).length || saving) return;
    setSaving(true);
    try {
      const result = await recordPtPayment({ domain, companyId, periodId: period.id, form });
      showToast("success", result.message || "Payment saved.");
      onSaved(result.period);
    } catch (problem) {
      const fields = problem.fields || {};
      setServerErrors(fields);
      if (!["type", "paid_on", "amount", "reference", "remarks", "period"].some((key) => fields[key])) showToast("error", problem.message || "Couldn't save the payment.");
    } finally {
      setSaving(false);
    }
  };

  const blocked = period && !period.can_record ? period.record_blocked_reason || "This period can't take a payment yet." : "";
  const previewColor = preview?.status === "PAID" ? "#067647" : "#B54708";

  return (
    <GlobalSidePanel
      open={open}
      onClose={onClose}
      title="Record PT Payment"
      subtitle={period ? [period.period_label, period.site, period.registration_no].filter(Boolean).join(" · ") : ""}
      bodySx={panelBodySx}
      footer={<PanelActionFooter onCancel={onClose} primaryLabel="Save Payment" primaryProps={{ onClick: save, loading: saving, disabled: !period || Boolean(blocked) }} />}
    >
      {loading && !period ? (
        <Loader info="Loading..." minHeight="40vh" />
      ) : loadError ? (
        <RequestInfoBanner tone="danger" title="Couldn't load the payment period." body={loadError} />
      ) : period && form ? (
        <Mui.Stack spacing={2}>
          {serverErrors.period || blocked ? <RequestInfoBanner tone="warning" body={serverErrors.period || blocked} /> : null}
          <PeriodHeader period={period} />
          <StatementCard domain={domain} companyId={companyId} period={period} />
          <FormSection title="Payment">
            <Field label={<>Payment type<Required /></>} error={error("type")}>
              <SelectField value={form.type} onChange={(value) => value && set("type")(value)} options={PAYMENT_TYPES} error={error("type")} />
            </Field>
            <Field label={<>Paid on<Required /></>} error={error("paid_on")}>
              <DateField value={form.paid_on} onChange={set("paid_on")} maxDate={moment().format("YYYY-MM-DD")} error={error("paid_on")} />
            </Field>
            <Field label={<>Amount paid (₹)<Required /></>} error={error("amount")}>
              <PlainInput type="number" value={form.amount} onChange={set("amount")} error={error("amount")} inputProps={{ min: 0, step: "0.01" }} />
            </Field>
            <Field label={<>Reference / challan no.<Required /></>} error={error("reference")}>
              <TextField value={form.reference} onChange={set("reference")} error={error("reference")} placeholder="e.g. KA-PT-CHL-558120" />
            </Field>
            <Field wide label="Remarks" error={error("remarks")}>
              <TextField value={form.remarks} onChange={set("remarks")} placeholder="—" />
            </Field>
            {preview?.line ? (
              <Mui.Typography sx={{ ...designType.captionText, gridColumn: "1 / -1", fontWeight: designWeights.semibold, color: previewColor }}>
                {preview.status === "PAID" && !preview.line.startsWith("✓") ? `✓ ${preview.line}` : preview.line}
              </Mui.Typography>
            ) : null}
          </FormSection>
          <RequestInfoBanner body="Paid short? Status becomes Partly paid; record a Balance payment later for the rest. A saved payment is never edited: use Reverse and record again." />
        </Mui.Stack>
      ) : null}
    </GlobalSidePanel>
  );
};

// ---------------- Period drawer ----------------

const ViewPanel = ({ open, domain, companyId, periodId, reloadKey, onClose, onRecord, onReversed }) => {
  const { loading, error, data: period } = usePeriod({ open, domain, companyId, periodId, reloadKey });
  const [target, setTarget] = useState(null);
  const [reason, setReason] = useState("");
  const [reasonError, setReasonError] = useState("");
  const [reversing, setReversing] = useState(false);

  const reverse = async () => {
    if (!reason.trim()) {
      setReasonError("Write why the payment is reversed");
      return;
    }
    if (reversing) return;
    setReversing(true);
    try {
      const result = await reversePtPayment({ domain, companyId, paymentId: target.id, reason });
      showToast("success", result.message || "Payment reversed.");
      setTarget(null);
      onReversed();
    } catch (problem) {
      if (problem.fields?.reason) setReasonError(problem.fields.reason);
      else {
        showToast("error", problem.message || "Couldn't reverse the payment.");
        setTarget(null);
        if (problem.status === 409) onReversed();
      }
    } finally {
      setReversing(false);
    }
  };

  return (
    <GlobalSidePanel
      open={open}
      onClose={onClose}
      title="PT Payment"
      subtitle={period ? [period.period_label, period.site, period.registration_no].filter(Boolean).join(" · ") : ""}
      bodySx={panelBodySx}
      footer={
        <Mui.Stack direction="row" spacing={1.5} justifyContent="flex-end" sx={{ width: "100%" }}>
          {period?.can_record ? <SecondaryButton onClick={onRecord}>Record payment</SecondaryButton> : null}
          <PrimaryButton onClick={onClose}>Close</PrimaryButton>
        </Mui.Stack>
      }
    >
      {loading && !period ? (
        <Loader info="Loading..." minHeight="40vh" />
      ) : error ? (
        <RequestInfoBanner tone="danger" title="Couldn't load the payment period." body={error} />
      ) : period ? (
        <Mui.Stack spacing={2}>
          <PeriodHeader period={period} />
          {!period.is_complete ? (
            <RequestInfoBanner
              body={`${period.months_finalised ?? 0} of ${period.months ?? "?"} months finalised. A ${(period.period_type_label || "period").toLowerCase()} period can be paid only after its last month is finalised.`}
            />
          ) : null}
          {!period.can_record && period.record_blocked_reason && period.is_complete ? <RequestInfoBanner body={period.record_blocked_reason} /> : null}
          <RequestDetailsSection
            title="AMOUNTS"
            fields={[
              { icon: FunctionsOutlined, label: period.is_complete ? "Payable" : "Collected so far", value: period.payable_label || money(period.payable), accent: indigo },
              { icon: CheckRounded, label: "Paid", value: money(period.paid), helper: period.paid_on ? `Last paid ${day(period.paid_on)}` : undefined, accent: green },
              { icon: PendingActionsOutlined, label: "Balance", value: money(period.balance), helper: period.due ? `Due ${day(period.due)}${period.overdue ? " · overdue" : ""}` : undefined, accent: orange },
            ]}
          />
          <StatementCard domain={domain} companyId={companyId} period={period} />
          <Mui.Box>
            <Mui.Typography sx={{ ...designType.sectionHeading, mb: 1 }}>Payments</Mui.Typography>
            <SimpleTable
              fit
              heads={[
                { label: "Type", width: 120 },
                { label: "Paid on", width: 100 },
                { label: "Amount (₹)", width: 90, align: "right" },
                { label: "Reference", width: 170 },
                { label: "Recorded by", width: 120 },
                { label: "", width: 70 },
              ]}
            >
              {period.payments.length ? (
                period.payments.map((payment) => (
                  <Mui.TableRow key={payment.id} sx={{ opacity: payment.reversed ? 0.6 : 1 }}>
                    <Mui.TableCell sx={tableCellSx}>
                      {payment.type_label}
                      {payment.reversed ? " · reversed" : ""}
                    </Mui.TableCell>
                    <Mui.TableCell sx={tableCellSx}>{day(payment.paid_on)}</Mui.TableCell>
                    <Mui.TableCell align="right" sx={{ ...tableCellSx, color: payment.signed_amount < 0 ? "#DC2626" : undefined }}>
                      {num(payment.signed_amount)}
                    </Mui.TableCell>
                    <Mui.TableCell sx={tableCellSx}>{payment.reference}</Mui.TableCell>
                    <Mui.TableCell sx={tableCellSx}>{payment.recorded_by || "—"}</Mui.TableCell>
                    <Mui.TableCell sx={tableCellSx}>
                      {payment.can_reverse ? (
                        <Mui.Link
                          component="button"
                          underline="hover"
                          onClick={() => {
                            setTarget(payment);
                            setReason("");
                            setReasonError("");
                          }}
                          sx={{ ...linkSx, color: "#DC2626" }}
                        >
                          Reverse
                        </Mui.Link>
                      ) : null}
                    </Mui.TableCell>
                  </Mui.TableRow>
                ))
              ) : (
                <EmptyRow colSpan={6} text="No payment recorded yet." />
              )}
            </SimpleTable>
          </Mui.Box>
        </Mui.Stack>
      ) : null}
      <GlobalConfirmDialog
        open={Boolean(target)}
        title="Reverse payment"
        onCancel={() => setTarget(null)}
        onConfirm={reverse}
        confirmLabel={reversing ? "Reversing…" : "Reverse"}
        cancelLabel="Keep"
        maxWidth="sm"
        danger
      >
        <Mui.Stack spacing={2}>
          <Mui.Typography sx={designType.helperText}>
            Reverse {target ? `${money(target.signed_amount)} (${target.reference})` : ""}? A reversal row (REV-…) is added, the original stays in the history and the balance goes back up. Record the correct payment afterwards.
          </Mui.Typography>
          <Field label={<>Reason<Required /></>} error={reasonError}>
            <TextField
              value={reason}
              onChange={(value) => {
                setReason(value);
                setReasonError("");
              }}
              error={reasonError}
              placeholder="e.g. Challan entered twice"
            />
          </Field>
        </Mui.Stack>
      </GlobalConfirmDialog>
    </GlobalSidePanel>
  );
};

// ---------------- Payments list ----------------

export const PtPaymentsTab = ({ domain, companyId, siteId, openPeriod, onChanged }) => {
  const { userDetails, topbarCompanyName } = useSelector((state) => state.tokenReducer);
  const [period, setPeriod] = useState("last_12");
  const [custom, setCustom] = useState({ from: "", to: "" });
  const [card, setCard] = useState(ALL);
  const [reloadKey, setReloadKey] = useState(0);
  const [list, setList] = useState({ loading: true, error: "", rows: [], cards: {}, range: {} });
  const [panel, setPanel] = useState(() => (openPeriod ? { kind: openPeriod.kind, id: openPeriod.id } : { kind: null, id: null }));
  const [viewKey, setViewKey] = useState(0);

  const { visibleHeaders, titleOverride } = useSavedViewSelector({
    domain,
    userId: userDetails?.id,
    storageKey: "pt_payments_saved_views",
    fullHeaders: ptPaymentHeader,
  });

  const customReady = period !== "custom" || (custom.from && custom.to && custom.from <= custom.to);
  useEffect(() => {
    if (!customReady) return undefined;
    const controller = new AbortController();
    setList((current) => ({ ...current, loading: true, error: "" }));
    fetchPtPayments({ domain, companyId, period, from: custom.from, to: custom.to, siteId, signal: controller.signal })
      .then((data) => setList({ loading: false, error: "", ...data }))
      .catch((error) => {
        if (!controller.signal.aborted) setList((current) => ({ ...current, loading: false, error: error.message || "Could not load PT payments." }));
      });
    return () => controller.abort();
  }, [domain, companyId, period, custom.from, custom.to, customReady, siteId, reloadKey]);

  const rows = card === ALL ? list.rows : list.rows.filter((row) => CARD_STATUSES[card].includes(row.status));
  const changed = () => {
    setReloadKey((key) => key + 1);
    setViewKey((key) => key + 1);
    onChanged();
  };

  const renderCell = (row, key) => {
    switch (key) {
      case "site":
        return <Mui.Box component="span" sx={{ fontWeight: designWeights.medium }}>{row.site}</Mui.Box>;
      case "employees":
        return num(row.employees);
      case "payable":
        return row.payable_label || num(row.payable);
      case "paid":
        return row.paid || row.paid_on ? num(row.paid) : "—";
      case "balance":
        return num(row.balance);
      case "paid_on":
        return day(row.paid_on);
      case "due":
        return <Mui.Box component="span" sx={{ color: row.overdue ? "#DC2626" : undefined, fontWeight: row.overdue ? designWeights.semibold : undefined }}>{day(row.due)}</Mui.Box>;
      case "status_label":
        return <ToneChip label={row.status_label} tone={row.status_tone} />;
      case "action":
        return (
          <Mui.Link
            component="button"
            underline="hover"
            onClick={(event) => {
              event.stopPropagation();
              setPanel({ kind: row.action === "RECORD_PAYMENT" ? "record" : "view", id: row.id });
            }}
            sx={linkSx}
          >
            {row.action === "RECORD_PAYMENT" ? "Record payment" : "View"}
          </Mui.Link>
        );
      default:
        return row[key] || "—";
    }
  };

  // Plain text for the download.
  const exportRows = rows.map((row) => ({
    ...row,
    payable: row.payable_label || row.payable,
    paid_on: row.paid_on || "",
    action: row.action === "RECORD_PAYMENT" ? "Record payment" : "View",
  }));

  return (
    <>
      <StatCardGrid
        value={card}
        onChange={(key) => setCard((current) => (current === key ? ALL : key))}
        cards={[
          { key: "topay", title: "To pay now", value: money(list.cards.to_pay_now), icon: PendingActionsOutlined, tone: "amber" },
          { key: "short", title: "Short paid", value: money(list.cards.short_paid), icon: ErrorOutlineOutlined, tone: "red" },
          { key: "paid", title: "Paid (last 2 periods)", value: money(list.cards.paid_last_2_periods), icon: CheckRounded, tone: "green" },
          { key: "collecting", title: "Collecting / not due", value: num(list.cards.collecting_or_not_due), icon: HourglassEmptyOutlined, tone: "blue" },
        ]}
      />
      <Mui.Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ sm: "flex-end" }} sx={{ mb: 1.5 }}>
        <FilterSelect label="Period" value={period} onChange={setPeriod} options={PERIOD_FILTERS} />
        {period === "custom" ? (
          <>
            <Mui.Box sx={{ width: { xs: "100%", sm: 170 } }}>
              <Mui.Typography sx={{ ...designType.captionText, mb: 0.5 }}>From</Mui.Typography>
              <PlainInput type="month" value={custom.from} onChange={(value) => setCustom((current) => ({ ...current, from: value }))} />
            </Mui.Box>
            <Mui.Box sx={{ width: { xs: "100%", sm: 170 } }}>
              <Mui.Typography sx={{ ...designType.captionText, mb: 0.5 }}>To</Mui.Typography>
              <PlainInput type="month" value={custom.to} onChange={(value) => setCustom((current) => ({ ...current, to: value }))} />
            </Mui.Box>
          </>
        ) : null}
        {list.range.from && list.range.to ? (
          <Mui.Typography sx={{ ...designType.captionText, pb: { sm: 1.25 } }}>
            {moment(`${list.range.from}-01`).format("MMM YYYY")} – {moment(`${list.range.to}-01`).format("MMM YYYY")}
          </Mui.Typography>
        ) : null}
      </Mui.Stack>
      {period === "custom" && !customReady ? (
        <Mui.Box sx={{ mb: 1.5 }}>
          <RequestInfoBanner body="Pick a From month and a To month (To on or after From)." />
        </Mui.Box>
      ) : null}
      {list.error ? (
        <Mui.Box sx={{ mb: 1.5 }}>
          <RequestInfoBanner
            tone="danger"
            title="Couldn't load PT payments."
            body={list.error}
            action={<SecondaryButton onClick={() => setReloadKey((key) => key + 1)} sx={{ whiteSpace: "nowrap" }}>Retry</SecondaryButton>}
          />
        </Mui.Box>
      ) : null}
      <StatutoryList
        title="PT Payments"
        headers={visibleHeaders}
        titleOverride={titleOverride}
        rows={exportRows}
        loading={list.loading}
        renderCell={(row, key) => renderCell(rows.find((item) => item.id === row.id) || row, key)}
        onRowClick={(row) => setPanel({ kind: "view", id: row.id })}
        selectedKey={panel.id}
        itemLabel="periods"
        searchPlaceholder="Search site, registration no. or period"
        metadata={{ "Company Name": topbarCompanyName || "N/A", "Report Generated On": moment().format("DD/MM/YYYY HH:mm:ss") }}
      />
      <Mui.Typography sx={{ ...designType.captionText, mt: 1 }}>One row per site per PT period (month, half-year or year).</Mui.Typography>
      <Mui.Box sx={{ mt: 1.5 }}>
        <RequestInfoBanner body="Payable = PT deducted from employees at the site for the period (after any refunds). Pay it to the state or local body, then record the payment here. Half-yearly and yearly periods can be paid only after their last month is finalised." />
      </Mui.Box>

      <RecordPanel
        open={panel.kind === "record" && panel.id != null}
        domain={domain}
        companyId={companyId}
        periodId={panel.id}
        onClose={() => setPanel({ kind: null, id: null })}
        onSaved={() => {
          setPanel((current) => ({ kind: "view", id: current.id }));
          changed();
        }}
      />
      <ViewPanel
        open={panel.kind === "view" && panel.id != null}
        domain={domain}
        companyId={companyId}
        periodId={panel.id}
        reloadKey={viewKey}
        onClose={() => setPanel({ kind: null, id: null })}
        onRecord={() => setPanel((current) => ({ ...current, kind: "record" }))}
        onReversed={changed}
      />
    </>
  );
};
